import {
    keepPreviousData,
    QueryClient,
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import { API_URL } from "@/config/constants";
import { invalidateClauseChecklistQueries } from "../clause-checklists/clause-checklist-query-keys";

import type {
    IOrganizationGroupCreate,
    IOrganizationGroupDetail,
    IOrganizationGroupListParams,
    IOrganizationGroupMemberCreate,
    IOrganizationGroupPage,
    IOrganizationGroupSkillExclusionCreate,
    IOrganizationGroupSkillMinimal,
    IOrganizationGroupUpdate,
    OrganizationGroupErrorDetail,
} from "./organization-groups.types";
import {
    OrganizationGroupError,
    OrganizationGroupRemovalUnconfirmedError,
    SkillExclusionRemovalUnconfirmedError,
    confirmedExcludedSkills,
} from "./organization-groups.types";

const API = `${API_URL}/admin/organization-groups`;

const skillExclusionMutationKey = (groupId: number) =>
    ["organization-group-skill-exclusions", groupId] as const;

const skillExclusionMutationScope = (groupId: number) => ({
    id: `organization-group-skill-exclusions:${groupId}`,
});

export const organizationGroupQueryKeys = {
    lists: ["admin-organization-groups"] as const,
    list: ({ page, size, search }: IOrganizationGroupListParams) =>
        [
            "admin-organization-groups",
            { page, size, search: search.trim() },
        ] as const,
    details: ["admin-organization-group-details"] as const,
    detail: (id: number) => ["admin-organization-group-details", id] as const,
    excludableSkills: (id: number) =>
        [
            ...organizationGroupQueryKeys.detail(id),
            "excludable-skills",
        ] as const,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const isGroupReference = (
    value: unknown
): value is { id: number; name: string } =>
    isRecord(value) &&
    typeof value.id === "number" &&
    typeof value.name === "string";

const isNumberArray = (value: unknown): value is number[] =>
    Array.isArray(value) &&
    value.every((id: unknown): id is number => typeof id === "number");

const organizationGroupErrorDetail = (
    value: unknown
): OrganizationGroupErrorDetail | null => {
    if (!isRecord(value) || typeof value.code !== "string") return null;

    switch (value.code) {
        case "organization_group_name_conflict":
        case "organization_group_shared_root_conflict":
        case "organization_group_reference_conflict":
            return { code: value.code };
        case "organization_group_assignment_conflict":
            return isGroupReference(value.group)
                ? { code: value.code, group: value.group }
                : null;
        case "organization_group_not_empty":
            return typeof value.member_count === "number" &&
                typeof value.assigned_skill_count === "number" &&
                typeof value.assigned_checklist_count === "number"
                ? {
                      code: value.code,
                      member_count: value.member_count,
                      assigned_skill_count: value.assigned_skill_count,
                      assigned_checklist_count: value.assigned_checklist_count,
                  }
                : null;
        case "organization_unavailable":
            return typeof value.organization_id === "number"
                ? {
                      code: value.code,
                      organization_id: value.organization_id,
                  }
                : null;
        case "skill_not_excludable":
            return isNumberArray(value.skill_ids)
                ? { code: value.code, skill_ids: value.skill_ids }
                : null;
        default:
            return null;
    }
};

const organizationGroupNameValidationDetail = (
    value: unknown
): OrganizationGroupErrorDetail | null => {
    if (!Array.isArray(value)) return null;
    const nameError = value.find((candidate) => {
        if (!isRecord(candidate) || !Array.isArray(candidate.loc)) return false;
        const type = candidate.type;
        return (
            candidate.loc[candidate.loc.length - 1] === "name" &&
            (type === "string_type" ||
                type === "string_too_short" ||
                type === "string_too_long" ||
                type === "value_error")
        );
    });
    return nameError ? { code: "organization_group_name_invalid" } : null;
};

const toOrganizationGroupError = (
    error: unknown
): OrganizationGroupError | null => {
    if (!axios.isAxiosError(error)) return null;
    const data: unknown = error.response?.data;
    if (!isRecord(data)) return null;
    const detail =
        organizationGroupErrorDetail(data.detail) ??
        (error.response?.status === 422
            ? organizationGroupNameValidationDetail(data.detail)
            : null);
    return detail
        ? new OrganizationGroupError(detail, error.response?.status)
        : null;
};

const runOrganizationGroupMutation = async <T>(request: () => Promise<T>) => {
    try {
        return await request();
    } catch (error) {
        const typedError = toOrganizationGroupError(error);
        if (typedError) throw typedError;
        throw error;
    }
};

export const listOrganizationGroups = async (
    params: IOrganizationGroupListParams
) => {
    const response = await axios.get<IOrganizationGroupPage>(API, {
        params,
    });
    return response.data;
};

export const getOrganizationGroup = async (id: number) => {
    const response = await axios.get<IOrganizationGroupDetail>(`${API}/${id}`);
    return response.data;
};

export const listOrganizationGroupExcludableSkills = async (id: number) => {
    const response = await axios.get<IOrganizationGroupSkillMinimal[]>(
        `${API}/${id}/excludable-skills`
    );
    return response.data;
};

export const excludeOrganizationGroupSkills = async (
    id: number,
    skillIds: number[]
) =>
    runOrganizationGroupMutation(async () => {
        const data: IOrganizationGroupSkillExclusionCreate = {
            skill_ids: skillIds,
        };
        const response = await axios.post<IOrganizationGroupDetail>(
            `${API}/${id}/excluded-skills`,
            data
        );
        return response.data;
    });

export const removeOrganizationGroupSkillExclusion = async (
    id: number,
    skillId: number
) =>
    runOrganizationGroupMutation(async () => {
        await axios.delete(`${API}/${id}/excluded-skills/${skillId}`);
    });

export const createOrganizationGroup = async (data: IOrganizationGroupCreate) =>
    runOrganizationGroupMutation(async () => {
        const response = await axios.post<IOrganizationGroupDetail>(API, data);
        return response.data;
    });

export const renameOrganizationGroup = async (
    id: number,
    data: IOrganizationGroupUpdate
) =>
    runOrganizationGroupMutation(async () => {
        const response = await axios.patch<IOrganizationGroupDetail>(
            `${API}/${id}`,
            data
        );
        return response.data;
    });

export const deleteOrganizationGroup = async (id: number) =>
    runOrganizationGroupMutation(async () => {
        await axios.delete(`${API}/${id}`);
    });

export const addOrganizationGroupMember = async (
    id: number,
    data: IOrganizationGroupMemberCreate
) =>
    runOrganizationGroupMutation(async () => {
        const response = await axios.post<IOrganizationGroupDetail>(
            `${API}/${id}/members`,
            data
        );
        return response.data;
    });

export const removeOrganizationGroupMember = async (
    id: number,
    organizationId: number
) =>
    runOrganizationGroupMutation(async () => {
        await axios.delete(`${API}/${id}/members/${organizationId}`);
    });

export const invalidateOrganizationGroupQueries = async (
    queryClient: QueryClient,
    options: { throwOnError?: boolean } = {}
) => {
    const detailInvalidations = options.throwOnError
        ? [
              queryClient.invalidateQueries(
                  {
                      queryKey: organizationGroupQueryKeys.details,
                      predicate: (query) => query.queryKey.length === 2,
                  },
                  options
              ),
              queryClient.invalidateQueries({
                  queryKey: organizationGroupQueryKeys.details,
                  predicate: (query) => query.queryKey.length > 2,
              }),
          ]
        : [
              queryClient.invalidateQueries(
                  { queryKey: organizationGroupQueryKeys.details },
                  options
              ),
          ];
    await Promise.all([
        queryClient.invalidateQueries(
            { queryKey: organizationGroupQueryKeys.lists },
            options
        ),
        ...detailInvalidations,
        queryClient.invalidateQueries({ queryKey: ["admin-skills"] }, options),
        queryClient.invalidateQueries({
            queryKey: ["admin-skills-core-count"],
        }),
        invalidateClauseChecklistQueries(queryClient, options),
    ]);
};

export const useOrganizationGroups = (params: IOrganizationGroupListParams) =>
    useQuery({
        queryKey: organizationGroupQueryKeys.list(params),
        queryFn: () => listOrganizationGroups(params),
        placeholderData: keepPreviousData,
    });

export const useOrganizationGroup = (id: number | null) =>
    useQuery({
        queryKey: organizationGroupQueryKeys.detail(id ?? 0),
        queryFn: () => getOrganizationGroup(id!),
        enabled: id !== null,
        staleTime: 30_000,
    });

export const useOrganizationGroupExcludableSkills = (id: number) =>
    useQuery({
        queryKey: organizationGroupQueryKeys.excludableSkills(id),
        queryFn: () => listOrganizationGroupExcludableSkills(id),
    });

export const useExcludeOrganizationGroupSkills = (groupId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: skillExclusionMutationKey(groupId),
        scope: skillExclusionMutationScope(groupId),
        mutationFn: (skillIds: number[]) =>
            excludeOrganizationGroupSkills(groupId, skillIds),
        onSuccess: async (group) => {
            await queryClient.cancelQueries({
                queryKey: organizationGroupQueryKeys.detail(groupId),
                exact: true,
            });
            queryClient.setQueryData(
                organizationGroupQueryKeys.detail(groupId),
                group
            );
            await invalidateOrganizationGroupQueries(queryClient);
        },
        onError: async (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                await queryClient.invalidateQueries({
                    queryKey: organizationGroupQueryKeys.detail(groupId),
                    exact: true,
                });
                return;
            }
            if (
                error instanceof OrganizationGroupError &&
                error.code === "skill_not_excludable"
            ) {
                await queryClient.invalidateQueries({
                    queryKey:
                        organizationGroupQueryKeys.excludableSkills(groupId),
                    exact: true,
                    refetchType: "all",
                });
            }
        },
    });
};

export type OrganizationGroupSkillExclusionConfirmation =
    | { kind: "absent"; group: IOrganizationGroupDetail }
    | { kind: "present"; group: IOrganizationGroupDetail }
    | { kind: "group_not_found" };

const confirmOrganizationGroupSkillExclusionRemoval = async (
    queryClient: QueryClient,
    groupId: number,
    skillId: number
): Promise<OrganizationGroupSkillExclusionConfirmation> => {
    const detailKey = organizationGroupQueryKeys.detail(groupId);
    try {
        await queryClient.cancelQueries({ queryKey: detailKey, exact: true });
        await queryClient.invalidateQueries({
            queryKey: detailKey,
            exact: true,
            refetchType: "none",
        });
        const group = await queryClient.fetchQuery({
            queryKey: detailKey,
            networkMode: "always",
            queryFn: async () => {
                const value = await getOrganizationGroup(groupId);
                if (!confirmedExcludedSkills(value)) {
                    throw new SkillExclusionRemovalUnconfirmedError(value);
                }
                return value;
            },
        });
        const excludedSkills = confirmedExcludedSkills(group);
        if (excludedSkills === null) {
            throw new SkillExclusionRemovalUnconfirmedError(group);
        }
        return {
            kind: excludedSkills.some((skill) => skill.id === skillId)
                ? "present"
                : "absent",
            group,
        };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return { kind: "group_not_found" };
        }
        if (error instanceof SkillExclusionRemovalUnconfirmedError) throw error;
        if (axios.isAxiosError(error)) {
            throw new SkillExclusionRemovalUnconfirmedError(error);
        }
        throw error;
    }
};

const invalidateSkillExclusionRelatedQueries = async (
    queryClient: QueryClient,
    groupId: number
) => {
    await Promise.allSettled([
        queryClient.invalidateQueries({
            queryKey: organizationGroupQueryKeys.lists,
        }),
        queryClient.invalidateQueries({
            queryKey: organizationGroupQueryKeys.excludableSkills(groupId),
            exact: true,
        }),
        queryClient.invalidateQueries({
            queryKey: ["admin-skills"],
        }),
        queryClient.invalidateQueries({
            queryKey: ["admin-skills-core-count"],
        }),
    ]);
};

const confirmAndRefreshSkillExclusionRemoval = async (
    queryClient: QueryClient,
    groupId: number,
    skillId: number,
    beforeConfirm?: () => Promise<void>
) => {
    try {
        await beforeConfirm?.();
        return await confirmOrganizationGroupSkillExclusionRemoval(
            queryClient,
            groupId,
            skillId
        );
    } finally {
        await invalidateSkillExclusionRelatedQueries(queryClient, groupId);
    }
};

export const useRemoveOrganizationGroupSkillExclusion = (groupId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: skillExclusionMutationKey(groupId),
        scope: skillExclusionMutationScope(groupId),
        mutationFn: (skillId: number) =>
            confirmAndRefreshSkillExclusionRemoval(
                queryClient,
                groupId,
                skillId,
                async () => {
                    try {
                        await removeOrganizationGroupSkillExclusion(
                            groupId,
                            skillId
                        );
                    } catch (error) {
                        if (
                            !axios.isAxiosError(error) ||
                            error.response?.status !== 404
                        ) {
                            throw error;
                        }
                    }
                }
            ),
    });
};

export const useRetryOrganizationGroupSkillExclusionRefresh = (
    groupId: number
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: skillExclusionMutationKey(groupId),
        scope: skillExclusionMutationScope(groupId),
        mutationFn: (skillId: number) =>
            confirmAndRefreshSkillExclusionRemoval(
                queryClient,
                groupId,
                skillId
            ),
    });
};

export const useIsOrganizationGroupSkillExclusionPending = (groupId: number) =>
    useIsMutating({
        mutationKey: skillExclusionMutationKey(groupId),
        exact: true,
    }) > 0;

export const useCreateOrganizationGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createOrganizationGroup,
        onSuccess: () => invalidateOrganizationGroupQueries(queryClient),
    });
};

export const useRenameOrganizationGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: number;
            data: IOrganizationGroupUpdate;
        }) => renameOrganizationGroup(id, data),
        onSuccess: (group) => {
            queryClient.setQueryData(
                organizationGroupQueryKeys.detail(group.id),
                group
            );
            return invalidateOrganizationGroupQueries(queryClient);
        },
    });
};

export const useDeleteOrganizationGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteOrganizationGroup,
        onSuccess: async (_, id) => {
            queryClient.removeQueries({
                queryKey: organizationGroupQueryKeys.detail(id),
                exact: true,
            });
            await invalidateOrganizationGroupQueries(queryClient);
        },
        onError: (error, id) => {
            if (
                error instanceof OrganizationGroupError &&
                (error.code === "organization_group_not_empty" ||
                    error.code === "organization_group_reference_conflict")
            ) {
                return queryClient.invalidateQueries({
                    queryKey: organizationGroupQueryKeys.detail(id),
                });
            }
        },
    });
};

export const useAddOrganizationGroupMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: number;
            data: IOrganizationGroupMemberCreate;
        }) => addOrganizationGroupMember(id, data),
        onSuccess: (group) => {
            queryClient.setQueryData(
                organizationGroupQueryKeys.detail(group.id),
                group
            );
            return invalidateOrganizationGroupQueries(queryClient);
        },
    });
};

export const useRemoveOrganizationGroupMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            id,
            organizationId,
        }: {
            id: number;
            organizationId: number;
        }) => removeOrganizationGroupMember(id, organizationId),
        onSuccess: async (_, { id, organizationId }) => {
            queryClient.setQueryData<IOrganizationGroupDetail>(
                organizationGroupQueryKeys.detail(id),
                (group) => {
                    if (!group) return group;
                    const members = group.members.filter(
                        (member) => member.id !== organizationId
                    );
                    return {
                        ...group,
                        members,
                        member_count: members.length,
                    };
                }
            );
            try {
                await invalidateOrganizationGroupQueries(queryClient, {
                    throwOnError: true,
                });
            } catch {
                throw new OrganizationGroupRemovalUnconfirmedError();
            }
        },
        onError: (error, { id }) => {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return queryClient.invalidateQueries({
                    queryKey: organizationGroupQueryKeys.detail(id),
                });
            }
        },
    });
};
